import { buildGARegistrationRequestHeaders, collectGARegistrationContext, type GARegistrationIdentifiers } from '@enter/utm';
import { getConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';
import { gtmClient } from '@/lib/gtm/client';

/**
 * 注册归因的 GA readiness。
 *
 * 登录回跳后的建号请求要带上 GA 的 client_id/session_id，但这两个值要等
 * Consent 有结论、GTM 加载完、gtag 回调后才有。等待只在产品区首请求前做一次：
 * 1200ms 上限、并发共享、超时或异常一律放行，绝不阻塞登录或页面渲染。
 * 是否等待由 App 按区域决定，这里只提供能力。
 */
export const GA_REGISTRATION_READINESS_TIMEOUT_MS = 1200;
/** Enter GA4 measurement id；`@enter/utm` 的 GA session cookie 名依赖同一个 id。 */
const GA_MEASUREMENT_ID = 'G-B92QNZNRMY';
const POLL_INTERVAL_MS = 50;

type GtagGet = (command: 'get', target: string, field: keyof GARegistrationIdentifiers, callback: (value: unknown) => void) => void;

let identifiers: GARegistrationIdentifiers = {};
let attempt: Promise<void> | null = null;

function analyticsAllowed(): boolean {
    return getConsentSnapshot().analyticsAllowed;
}

function currentConsent() {
    const { analyticsAllowed: analytics, advertisingAllowed: advertising } = getConsentSnapshot();
    return { analytics, advertising };
}

function hasCompleteIdentifiers(): boolean {
    const context = collectGARegistrationContext(currentConsent(), identifiers);
    return Boolean(context.client_id && context.session_id);
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => globalThis.setTimeout(resolve, Math.max(0, ms)));
}

/** 在共享截止时间内等待；保留明确的不可用结果，成功时清理计时器。 */
async function withinDeadline<T>(promise: Promise<T>, deadline: number): Promise<T | undefined> {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return undefined;
    return new Promise(resolve => {
        const timeout = setTimeout(() => resolve(undefined), remaining);
        void promise.then(
            value => { clearTimeout(timeout); resolve(value); },
            () => { clearTimeout(timeout); resolve(undefined); },
        );
    });
}

/** 未决时只等到本次请求截止时间，超时也释放订阅。 */
function consentResolved(deadline: number): Promise<void> {
    if (getConsentSnapshot().resolution !== 'pending' || Date.now() >= deadline) return Promise.resolve();
    return new Promise(resolve => {
        const finish = () => {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
        };
        const unsubscribe = subscribeConsent(() => {
            if (getConsentSnapshot().resolution !== 'pending') finish();
        });
        const timeout = setTimeout(finish, Math.max(0, deadline - Date.now()));
        if (getConsentSnapshot().resolution !== 'pending') finish();
    });
}

function requestIdentifiers(): void {
    const gtag = (window as unknown as { gtag?: GtagGet }).gtag;
    if (!gtag) return;
    for (const field of ['client_id', 'session_id'] as const) {
        gtag('get', GA_MEASUREMENT_ID, field, (value) => {
            // 回调可能晚于撤回授权，此时丢弃。
            if ((typeof value === 'string' || typeof value === 'number') && analyticsAllowed()) {
                identifiers = { ...identifiers, [field]: value };
            }
        });
    }
}

async function run(timeoutMs: number): Promise<void> {
    const deadline = Date.now() + Math.max(0, timeoutMs);
    try {
        await consentResolved(deadline);
        if (!analyticsAllowed()) { identifiers = {}; return; }
        if (hasCompleteIdentifiers()) return;

        const gtmReady = await withinDeadline(gtmClient.whenReady(), deadline);
        if (gtmReady === false) return;
        requestIdentifiers();
        while (Date.now() < deadline) {
            if (!analyticsAllowed()) { identifiers = {}; return; }
            if (hasCompleteIdentifiers()) return;
            await delay(Math.min(POLL_INTERVAL_MS, deadline - Date.now()));
        }
    } catch {
        // readiness 只能改善归因，不能阻塞登录。
    }
}

/** 每个页面生命周期只等一次；并发请求共享同一次等待，结算后直接复用。 */
export function ensureGARegistrationReadiness(timeoutMs = GA_REGISTRATION_READINESS_TIMEOUT_MS): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    attempt ??= run(timeoutMs);
    return attempt;
}

/** 生成注册归因请求头；未授权时同步清掉内存里的标识。 */
export function getRegistrationRequestHeaders(): Record<string, string> {
    if (!analyticsAllowed()) identifiers = {};
    return buildGARegistrationRequestHeaders(currentConsent(), { ...identifiers });
}

/** 仅用于单元测试。 */
export function resetGARegistrationReadinessForTests(): void {
    identifiers = {};
    attempt = null;
}
